import React, { ReactElement, useContext } from 'react'
import Sync from './Sync'
import Prepare from './Prepare'
import { faAward, faBolt, faCalendarDay, faCalendarWeek, faGaugeHigh,
    faKey, faListAlt, faListCheck, faMapMarkerAlt, faMicrophoneLines, faMoneyBill,
    faMoon, faQuoteLeft, faRecycle, faSignOutAlt, faStar, faICursor } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp, library } from '@fortawesome/fontawesome-svg-core'
import { AppContext } from '../contexts/AppContext'
import { clearAllCaches } from '../contexts/app/dispatchers'
import { currentCommitHash, currentBuildTimestamp, hasBuildTimestamp } from '../helpers/environment'
import { Link } from 'react-router-dom'
library.add(faSignOutAlt, faQuoteLeft, faMoneyBill, faMapMarkerAlt, faMicrophoneLines, faICursor,
    faStar, faBolt, faAward, faCalendarDay, faCalendarWeek, faListAlt, faRecycle, faListCheck, faKey, faMoon, faGaugeHigh)

const UserPropertyToggle = (props: { field: string, title: string, description?: string, icon?: IconProp }): ReactElement => {
    const { appState, appDispatch } = useContext(AppContext)
    const savedValue = !!appState.user?.row?.[props.field]
    const updatedValue = appState.user?.updated?.[props.field]
    return <div className="thumbnail thumbnail-static">
        <h3>{ props.icon && <FontAwesomeIcon icon={ props.icon } /> }{props.title}</h3>
        { props.description && <div className="tip">{props.description}</div>}
        <input
            type="checkbox"
            id={ props.field }
            disabled={ updatedValue !== undefined }
            checked={ updatedValue !== undefined ? !!updatedValue : savedValue }
            onChange={ () => appDispatch({
                type: 'UPDATE_USER_FIELD',
                field: props.field,
                value: !appState.user?.row?.[props.field] }) } />
        <label htmlFor={ props.field }>{props.title}</label>
    </div>
}

const UserFieldToggle = (props: { field: string }): ReactElement => {
    const { appState, appDispatch, hasLoginScope } = useContext(AppContext)
    const setting = 'write_' + props.field
    const savedValue = !!appState.user?.row?.[setting]
    const updatedValue = appState.user?.updated?.[setting]
    const field = WRITE_FIELDS[props.field]
    const title = field.title || props.field.substr(0, 1).toUpperCase() + props.field.substr(1)
    if (field.adminOnly && !hasLoginScope('admin')) {
        return <></>
    }
    return <p>
        <input
            type="checkbox"
            id={ setting }
            disabled={ updatedValue !== undefined }
            checked={ updatedValue !== undefined ? !!updatedValue : savedValue }
            onChange={ () => appDispatch({
                type: 'UPDATE_USER_FIELD',
                field: setting,
                value: !appState.user?.row?.[setting] }) } />
        <label htmlFor={ setting }><FontAwesomeIcon icon={ field.icon } />{title}<span className="tip"> - {field.description}</span></label>
    </p>
}

const WRITE_FIELDS: {
    [field: string]: {
        icon: IconProp
        title?: string
        description?: string
        adminOnly?: true
    }
} = {
    title: { icon: 'quote-left', description: 'A short header for each entry' },
    summary: { icon: 'list-alt', description: 'a bullet-point overview of the day' },
    loc: { icon: 'map-marker-alt', title: 'Location', description: 'where were you?' },
    rating: { icon: 'star', description: 'how enjoyable was the event?' },
    energy: { icon: 'bolt', description: 'how active were you?' },
    importance: { icon: 'award', description: 'how memorable was this event?' },
    price: { icon: 'money-bill', description: 'how much did you spend during the event?' },
    // voice: { icon: 'microphone-lines', description: 'automatically transcribe audio logs', adminOnly: true },
    date: { icon: 'calendar-day', description: 'change the entry\'s stored day' },
    type: { icon: 'calendar-week', title: 'Spans', description: 'mark (and easily analyse) periods of time, not just one day.' }
}

const DARK_MODE_OPTIONS: { value: string, label: string }[] = [
    { value: 'light', label: 'Light mode' },
    { value: 'system', label: 'Match system theme' },
    { value: 'dark', label: 'Dark mode' }
]

const DARK_MODE_KEY = 'dark_mode'

const DarkModeToggle = (): ReactElement => {
    const [current, setCurrent] = React.useState(() => localStorage.getItem(DARK_MODE_KEY) ?? 'system')
    const onChange = (value: string): void => {
        localStorage.setItem(DARK_MODE_KEY, value)
        window.dispatchEvent(new StorageEvent('storage', { key: DARK_MODE_KEY, newValue: value }))
        setCurrent(value)
    }
    return <div className="thumbnail thumbnail-static">
        <h3><FontAwesomeIcon icon="moon" />Dark mode</h3>
        <div className="tip">Change Trove&apos;s visual style on this device, if you prefer a darker look.</div>
        {DARK_MODE_OPTIONS.map(({ value, label }) =>
            <span key={ value } className="dark-mode-option">
                <input
                    type="radio"
                    id={ `dark_mode_${value}` }
                    name="dark_mode"
                    value={ value }
                    checked={ current === value }
                    onChange={ () => onChange(value) } />
                <label htmlFor={ `dark_mode_${value}` }>{label}</label>
            </span>
        )}
    </div>
}

const UserSettings = (): ReactElement => {
    const { appState, appDispatch, isUserType } = useContext(AppContext)
    return <>
        <h2>Account control</h2>

        <div className="thumbnail-list settings-thumbnail-list">

            <Prepare />

            <div className="thumbnail thumbnail-static">
                <h3><FontAwesomeIcon icon="i-cursor" />Text fine-tuning</h3>
                <Link to="/settings/snippets">Configure text blocks</Link> which Trove should treat specially.
                <div className="tip">E.g. names or tags which should be combined; phrases to be ignored; and more.</div>
            </div>

            <div className="thumbnail thumbnail-static">
                <h3><FontAwesomeIcon icon="recycle" />Clear cache</h3>
                If you wish, you can <span
                    onClick={ () => clearAllCaches(appDispatch) }
                    className={ `clickable ${appState.notification?.message === 'Caches cleared successfully' ? ' clicked' : ''}` }>
                    clear data
                </span> the site has stored in your browser.
                <div className="tip">
                    This should be done periodically.
                    You will need an Internet connection to access existing entries.
                    Unsaved entries will never be cleared.
                </div>
            </div>

            <div className="thumbnail thumbnail-static">
                <h3><FontAwesomeIcon icon="list-check" />Extra fields</h3>
                <div className="tip">If you wish, you can store more than just text in an entry.</div>
                { Object.keys(WRITE_FIELDS).map(field => <UserFieldToggle key={ field } field={ field } />) }
            </div>

            <DarkModeToggle />

            <UserPropertyToggle
                field="is_power_user"
                icon="gauge-high"
                title="Power user"
                description="Power users can access extra, potentially overwhelming, statistics around the site." />

            <div className="thumbnail thumbnail-static">
                <h3><FontAwesomeIcon icon="key" />Password reset</h3>
                <div className="tip">
                    You can reset your password using the link on the Auth0 Login screen when{' '}
                    <FontAwesomeIcon icon="sign-out-alt" className="logout-icon" />&nbsp;logged out.
                </div>
            </div>

            { isUserType('power') && <Sync /> }

            <div className="thumbnail thumbnail-static tip">
                Current Trove version: {currentCommitHash()}{hasBuildTimestamp() && `, built on ${currentBuildTimestamp()}`}.
            </div>
        </div>
    </>
}

export default UserSettings
